/**
 * Training SOP — Field Leader track.
 *
 * The most distinct track of the set, and the one written for the least
 * software-heavy audience. Assume the reader is standing on a job site holding
 * a phone, not sitting at a desk. Short sentences, plain words, no jargon, and
 * every "that looks broken" case answered before they have to ask.
 *
 * A field leader's world is deliberately narrow: one Projects tab, only the
 * jobs they're assigned to, no cost figures, and one job to do well — the
 * daily log. Say that up front so the narrowness reads as designed rather
 * than as something withheld.
 *
 * Verified against the code (2026-07-25 sweep) and the product owner's ownership answers.
 */
import { OWNERSHIP, type Track } from '../types';

export const FIELD_LEADER_TRACK: Track = {
  id: 'field-leader',
  label: 'Field Leader',
  audience:
    'Field leaders running crews on site. Mostly used from a phone. You only need this one section — the daily log is your job here.',
  suggestedFor: { anyRole: ['FieldLeader'] },
  sections: [
    // ── 1. What you'll see ──────────────────────────────────────────────────
    {
      id: 'fl-what-you-see',
      title: 'What you\'ll see when you sign in',
      intro: 'Your view is smaller than the office\'s on purpose. Nothing is missing.',
      topics: [
        {
          id: 'fl-your-view',
          title: 'One tab, your jobs only',
          summary:
            'You get a single tab — Projects — and it lists only the jobs you have been assigned to lead. The office sees company-wide screens for scheduling, safety and reporting; you do not need them, so they are not there.',
          watchOut: [
            'A job you are working on is missing? You have not been assigned to it yet. Ask your PM to add you as a field leader on that job.',
            'You will not see any dollar figures anywhere. That is deliberate — cost information is not part of the field view.',
          ],
          deepLink: '/projects',
        },
        {
          id: 'fl-on-your-phone',
          title: 'Using it on a phone',
          summary:
            'The app works on a phone. On a small screen the list of job sections moves from the side to a row across the top that you can swipe along.',
          watchOut: [
            'If the table looks cut off at the side, swipe it sideways — there is more to the right.',
          ],
        },
      ],
    },

    // ── 2. The daily log ────────────────────────────────────────────────────
    {
      id: 'fl-daily-log',
      title: 'The daily log',
      intro:
        'This is your job in the Project Tracker. It is the record of what actually happened on site — and months later, in an argument about a delay, it is the thing everyone reads.',
      topics: [
        {
          id: 'fl-start-log',
          title: 'Starting today\'s log',
          summary:
            'One button. There is one log per job per day, so if you already started one today it picks up where you left off instead of making a second one.',
          owner: OWNERSHIP.dailyLogs,
          steps: [
            'Open your job from **Projects**.',
            'Go to the **Daily Logs** section.',
            'Tap **Start Today\'s Log**.',
          ],
          watchOut: [
            'Your name goes on the log automatically as the author.',
          ],
          access: { anyPermission: ['daily_logs.edit', 'projects.edit'] },
        },
        {
          id: 'fl-fill-log',
          title: 'Filling it in through the day',
          summary:
            'Work through it as the day goes. Nothing has to be done in one sitting — save a draft whenever you like and come back.',
          steps: [
            'Tap the **⛅ weather button** to fill in today\'s weather from the job address, then check it looks right.',
            'Add your **crews** — company, trade, how many workers, how many hours. The man-hours add themselves up.',
            'Write **work performed**, by area.',
            'Add **quantities installed** and any **photos**.',
            'Fill in **delays** and **safety notes** — these are the ones that matter most later.',
            'Tap **Save draft** as you go.',
          ],
          watchOut: [
            'Weather only works for today, and only if the job has a site address saved. If it says there is no address, ask your PM to add one.',
            'The **Equipment on site** panel is just for information — it comes from the equipment records and you cannot change it here.',
          ],
          access: { anyPermission: ['daily_logs.edit', 'projects.edit'] },
        },
        {
          id: 'fl-photos',
          title: 'Adding photos',
          summary:
            'Photos go straight into the job\'s folder on SharePoint. You can take them there and then, or add them later from your camera roll.',
          steps: [
            'In the log, tap **＋ Add photos**.',
            'Choose **Take Photo**, or pick from your photo library — you can select several at once.',
            'They appear as thumbnails on the log, and on the job’s **Photos** tab with everyone else’s.',
          ],
          watchOut: [
            'Photos upload using **your own Microsoft account**, so you need access to that job\'s SharePoint folder. Being assigned to the job in the Project Tracker is not the same thing. If uploads fail, ask your PM to share the job folder with you — this is the single most common problem field leaders hit.',
            'Removing a photo here only unlinks it from the log. The file stays in the job folder.',
          ],
          access: { anyPermission: ['daily_logs.edit', 'projects.edit'] },
        },
        {
          id: 'fl-pours',
          title: 'Recording a concrete pour',
          summary:
            'If concrete went in today, check it off on the log. That tells the office the pour actually happened, so nobody has to chase you for it.',
          steps: [
            'In the log, find **Concrete pours placed today**.',
            'Check the pour that went in.',
            'Enter the **actual cubic yards** placed.',
            'Save or submit — the pour is marked as poured automatically.',
          ],
          watchOut: [
            'Only pours the office has already confirmed with the supplier appear here. If the pour you did is not listed, tell the office rather than working around it.',
          ],
          access: { anyPermission: ['daily_logs.edit', 'projects.edit'] },
        },
        {
          id: 'fl-mix-id',
          title: 'The Mix ID on the pre-pour checklist',
          summary:
            'The Mix ID you write on the pre-pour form is now checked against the mixes approved for that job. Type it as it appears on the ticket or the mix design — not from memory.',
          owner: OWNERSHIP.concreteMixDesigns,
          steps: [
            'Fill in the Mix ID on the pre-pour checklist exactly as it is written on the paperwork.',
            'Send the form as normal. Nothing extra to do.',
          ],
          watchOut: [
            'Case, spaces and dashes do not matter — `408SP2NH`, `408-SP2NH` and `408 sp2nh` all read the same. Getting the actual characters right does matter.',
            'If it does not match what has been approved for the job, the office sees a flag and will come back to you. It never stops the pour, and it is not a mark against you — it is usually either a typo or a mix nobody has recorded yet.',
          ],
          access: { anyPermission: ['daily_logs.edit', 'projects.edit'] },
        },
        {
          id: 'fl-submit',
          title: 'Submitting at the end of the day',
          summary:
            'Submitting saves whatever you have typed and then locks the log for good.',
          steps: [
            'Check the log over — especially crews, hours, delays and safety notes.',
            'Tap **Submit log**.',
          ],
          watchOut: [
            '**Once submitted, a log cannot be changed or unlocked by anyone.** Read it over before you submit. If you are not sure, leave it as a draft and finish it tomorrow morning.',
            'You do not need to save first. Submit saves and then locks, in that order.',
          ],
          access: { anyPermission: ['daily_logs.edit', 'projects.edit'] },
        },
      ],
    },

    // ── 3. Asking a question on the job ─────────────────────────────────────
    {
      id: 'fl-rfis',
      title: 'Asking a question about the drawings',
      intro:
        'When the drawings do not answer something, you raise an RFI. New as of August 2026 — field leaders can now start one themselves instead of phoning it in.',
      topics: [
        {
          id: 'fl-raise-rfi',
          title: 'Raising an RFI',
          summary:
            'An RFI is a written question about the drawings or the spec. Open your job, go to RFIs, and use **+ New RFI** — there is also a **+ New RFI** button at the top of the job. Fill in the subject and the question; the RFI number fills itself in.',
          steps: [
            'Open your job and choose **RFIs** (or use **+ New RFI** at the top of the job, which opens the form directly).',
            'Leave the RFI number as it is — it follows on from the last one on the job.',
            'Write a short subject, then the question itself. Say what you are looking at and what is unclear.',
            'Add the spec section and who you think should answer, if you know them. Leave them blank if you do not.',
            'Press **Create RFI**.',
          ],
          watchOut: [
            'Creating the RFI does **not** send it to anyone. It puts the question on the job for your PM to send on — that step is theirs, not yours.',
            'Only jobs you are assigned to lead will accept an RFI from you. If the job is not in your list, you are not assigned to it yet.',
            'You cannot answer an RFI, including your own. The answer comes back through the PM.',
            'Attaching files and sending the RFI are your PM\'s steps, not yours. Put the drawing or detail number in the question itself so they know what to mark up.',
          ],
          owner: OWNERSHIP.rfis,
          deepLink: '/projects',
          // Field leaders hold rfis.create; PMs/PEs hold projects.edit. Anyone
          // without either genuinely cannot do this, so the topic goes away.
          access: { anyPermission: ['rfis.create', 'projects.edit'] },
        },
        {
          id: 'fl-rfi-after',
          title: 'What happens after you raise one',
          summary:
            'Your RFI shows on the job\'s RFI list right away, marked Open. Your PM sends it to whoever needs to answer, and the answer is recorded against the same RFI so the whole exchange stays in one place.',
          watchOut: [
            'An RFI you raised staying Open for a while is normal — it is waiting on an outside answer, not lost.',
            'You will see the change orders raised on your job, but not what they are worth. That is deliberate, the same as everywhere else in your view.',
          ],
          access: { anyPermission: ['rfis.create', 'projects.edit'] },
        },
      ],
    },

    // ── 4. When something looks wrong ───────────────────────────────────────
    {
      id: 'fl-troubleshooting',
      title: 'When something looks wrong',
      intro: 'The four things field leaders run into, and what each one actually means.',
      topics: [
        {
          id: 'fl-job-missing',
          title: '"My job isn\'t in the list"',
          summary:
            'You only see jobs you are assigned to lead. If one is missing, the assignment has not been made yet.',
          steps: ['Ask your PM to add you as a field leader on that job. You will see it right away — no need to sign out and back in.'],
        },
        {
          id: 'fl-cant-start-log',
          title: '"I can\'t start a daily log"',
          summary:
            'Two reasons, and they look the same from where you are standing.',
          watchOut: [
            'You may not be assigned to that job — you can only write logs on your own jobs.',
            'Or the job may not have its real job number yet. If the job number starts with **SYSTAC-** or **ClosedWon-**, the office still has to finish setting it up. Tell your PM.',
          ],
        },
        {
          id: 'fl-photos-failing',
          title: '"My photos won\'t upload"',
          summary:
            'Almost always SharePoint access, not the app. You need permission to the job\'s folder under your own Microsoft account.',
          steps: ['Ask your PM to share the job folder with you. Once they have, try again — nothing is lost, the rest of your log is still there.'],
        },
        {
          id: 'fl-no-costs',
          title: '"I can\'t see any costs or budgets"',
          summary:
            'Correct — that is how it is set up. Cost and budget information is not part of the field view, and there is nothing to switch on.',
        },
      ],
    },
  ],
};
